import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Mail, Lock, KeyRound, ShieldCheck, ArrowLeft, Waves } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useToast } from '../context/ToastContext';

const ForgotPassword = () => {
    const [step, setStep] = useState(1); // 1: Email, 2: OTP & New Password
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [passwords, setPasswords] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const { showToast } = useToast();
    const navigate = useNavigate();
    const otpRefs = useRef([]);
    const isOtpComplete = otp.every(digit => digit !== '');

    // Balurghat Swimming Pool slides - sharing the same as Login page
    const poolImages = useMemo(() => [
        {
            title: 'Secure Your Account',
            description: 'Reset your password to keep your profile protected',
            imageUrl: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
        },
        {
            title: 'Modern Facilities',
            description: 'Enjoy our state-of-the-art swimming infrastructure',
            imageUrl: 'https://images.unsplash.com/photo-1530549387789-4c1017266635?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
        },
        {
            title: 'Safe & Verified',
            description: 'Your security is our top priority',
            imageUrl: 'https://images.unsplash.com/photo-1544717297-fa95b6ee9643?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
        }
    ], []);

    // Auto-advance slideshow
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % poolImages.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [poolImages.length]);

    const handleOtpChange = (index, value) => {
        if (isNaN(value)) return;
        const newOtp = [...otp];
        newOtp[index] = value.substring(value.length - 1);
        setOtp(newOtp);

        // Move to next input if value is entered
        if (value && index < 5) {
            otpRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1].focus();
        }
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}api/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await response.json();
            if (response.ok) {
                showToast(data.message || 'OTP sent to your email', 'success');
                setStep(2);
            } else {
                showToast(data.message || 'Failed to send OTP', 'error');
            }
        } catch (error) {
            showToast('Something went wrong. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        const otpString = otp.join('');
        if (otpString.length !== 6) {
            showToast('Please enter the 6-digit OTP', 'error');
            return;
        }
        if (passwords.newPassword !== passwords.confirmPassword) {
            showToast('Passwords do not match', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}api/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    otp: otpString,
                    password: passwords.newPassword,
                    confirm_password: passwords.confirmPassword
                })
            });
            const data = await response.json();
            if (response.ok) {
                showToast('Password changed successfully!', 'success');
                setTimeout(() => navigate('/login'), 2000);
            } else {
                showToast(data.message || 'Failed to reset password', 'error');
            }
        } catch (error) {
            showToast('Something went wrong. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-vh-100 d-flex flex-column bg-light login-page forgot-password-page">
            <Header />
            
            <div className="flex-grow-1 d-flex align-items-center justify-content-center py-5">
                <div className="container">
                    <div className="row rounded overflow-hidden shadow-lg" style={{ minHeight: '600px' }}>
                        
                        {/* Left Side - Slideshow (Same as Login) */}
                        <div className="col-lg-6 d-none d-lg-block p-0">
                            <div className="h-100 position-relative overflow-hidden">
                                <div className="h-100 position-relative">
                                    {poolImages.map((image, index) => (
                                        <div
                                            key={index}
                                            className={`position-absolute w-100 h-100 ${
                                                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                                            }`}
                                            style={{
                                                backgroundImage: `url(${image.imageUrl})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                backgroundColor: '#4a90e2',
                                                transition: 'opacity 1.5s ease-in-out, transform 1.5s ease-in-out',
                                                transform: index === currentImageIndex ? 'scale(1)' : 'scale(1.1)'
                                            }}
                                        />
                                    ))}
                                    <div className="position-absolute w-100 h-100" style={{
                                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))'
                                    }} />
                                </div>

                                <div className="h-100 d-flex flex-column justify-content-center align-items-center text-white p-5 position-relative" style={{ zIndex: 5 }}>
                                    <div className="text-center">
                                        <Waves size={60} className="mb-4 login-float" />
                                        <h2 className="fw-bold mb-3 login-slide-in-top" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
                                            {poolImages[currentImageIndex].title}
                                        </h2>
                                        <p className="lead mb-4 login-slide-in-bottom" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.5)', fontSize: '1.1rem' }}>
                                            {poolImages[currentImageIndex].description}
                                        </p>
                                        
                                        <div className="d-flex justify-content-center gap-2 mt-3">
                                            {poolImages.map((_, idx) => (
                                                <span
                                                    key={idx}
                                                    onClick={() => setCurrentImageIndex(idx)}
                                                    style={{
                                                        width: idx === currentImageIndex ? '24px' : '8px',
                                                        height: '8px',
                                                        borderRadius: '4px',
                                                        background: idx === currentImageIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease',
                                                        display: 'inline-block'
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Form */}
                        <div className="col-lg-6 col-md-10 col-11 mx-auto bg-white p-4 p-md-5">
                            <div className="h-100 d-flex flex-column justify-content-center">
                                <Link to="/login" className="text-decoration-none text-muted d-flex align-items-center gap-2 mb-4 hover-primary">
                                    <ArrowLeft size={18} />
                                    <span>Back to Login</span>
                                </Link>

                                <div className="text-center mb-4">
                                    <div className="text-primary mb-3">
                                        <KeyRound size={40} />
                                    </div>
                                    <h3 className="fw-bold mb-2">Forgot Password?</h3>
                                    <p className="text-muted small">
                                        {step === 1 
                                            ? "Enter your email and we'll send you an OTP to reset your password."
                                            : `Enter the 6-digit code sent to ${email}`}
                                    </p>
                                </div>

                                {step === 1 ? (
                                    <form onSubmit={handleSendOtp} className="login-fade-in">
                                        <div className="mb-4">
                                            <label htmlFor="email" className="form-label fw-semibold">Email Address</label>
                                            <div className="input-group shadow-sm">
                                                <span className="input-group-text bg-light border">
                                                    <Mail size={18} className="text-muted" />
                                                </span>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="Enter your registered email"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100 py-2 fw-bold shadow-sm"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Sending...' : 'SEND OTP'}
                                        </button>
                                    </form>
                                ) : (
                                    <form onSubmit={handleResetPassword} className="login-fade-in">
                                        <div className="mb-4">
                                            <label className="form-label fw-semibold d-block text-center mb-3">OTP Verification</label>
                                            <div className="d-flex justify-content-center gap-2">
                                                {otp.map((digit, index) => (
                                                    <input
                                                        key={index}
                                                        ref={el => otpRefs.current[index] = el}
                                                        type="text"
                                                        maxLength="1"
                                                        value={digit}
                                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                                        className="form-control text-center fw-bold bg-light shadow-sm"
                                                        style={{ width: '100%', maxWidth: '45px', height: '45px', borderRadius: '8px' }}
                                                        required
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="newPassword" className="form-label fw-semibold">New Password</label>
                                            <div className="input-group shadow-sm">
                                                <span className="input-group-text bg-light border">
                                                    <Lock size={16} className="text-muted" />
                                                </span>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="newPassword"
                                                    value={passwords.newPassword}
                                                    onChange={(e) => setPasswords({...passwords, newPassword: e.target.value})}
                                                    placeholder={isOtpComplete ? "Min. 6 characters" : "Enter OTP first..."}
                                                    required
                                                    disabled={!isOtpComplete}
                                                />
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="confirmPassword" className="form-label fw-semibold">Confirm Password</label>
                                            <div className="input-group shadow-sm">
                                                <span className="input-group-text bg-light border">
                                                    <ShieldCheck size={16} className="text-muted" />
                                                </span>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="confirmPassword"
                                                    value={passwords.confirmPassword}
                                                    onChange={(e) => setPasswords({...passwords, confirmPassword: e.target.value})}
                                                    placeholder={isOtpComplete ? "Repeat new password" : "Enter OTP first..."}
                                                    required
                                                    disabled={!isOtpComplete}
                                                />
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100 py-2 fw-bold shadow-sm"
                                            disabled={isLoading || !isOtpComplete}
                                        >
                                            {isLoading ? 'Processing...' : 'CHANGE PASSWORD'}
                                        </button>
                                        
                                        <div className="text-center mt-3">
                                            <button 
                                                type="button" 
                                                className="btn btn-link text-decoration-none text-muted p-0 small"
                                                onClick={() => setStep(1)}
                                            >
                                                Resend OTP?
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ForgotPassword;
